<!--
  AgentStatusCard.vue — Summary card showing an agent's current state.
  
  Used in the Command Center dashboard: one card per agent, showing
  their operational status (monitoring, acting, idle), last action,
  and quick KPIs.
  
  Props:
    agent    — 'centinela' | 'jefe' | 'supervisor' | 'ingeniero'
    status   — 'active' | 'monitoring' | 'acting' | 'idle' | 'error'
    summary  — Short one-liner ("Monitoring 12 cameras, 0 alerts")
    kpis     — Array of { label, value, trend? } for inline metrics
    lastAction — { text, timestamp } last thing the agent did
-->
<template>
  <VCard rounded="lg" :class="`agent-accent--${agent}`">
    <VCardItem>
      <template #prepend>
        <VAvatar
          :color="agentColor"
          size="44"
          variant="tonal"
        >
          <VIcon size="24">{{ agentIcon }}</VIcon>
        </VAvatar>
      </template>

      <VCardTitle class="d-flex align-center gap-2">
        {{ agentLabel }}
        <StatusBadge
          :severity="statusSeverity"
          :label="statusLabel"
          :pulse="isLive"
          size="small"
        />
      </VCardTitle>

      <VCardSubtitle v-if="summary">
        {{ summary }}
      </VCardSubtitle>
    </VCardItem>

    <!-- KPI Row -->
    <VCardText v-if="kpis && kpis.length > 0">
      <div class="d-flex gap-4 flex-wrap">
        <div v-for="(kpi, idx) in kpis" :key="idx" class="kpi-item">
          <span class="kpi-value text-h6 font-weight-bold">
            {{ kpi.value }}
          </span>
          <span class="kpi-label narrative-caption d-block">
            {{ kpi.label }}
          </span>
          <span
            v-if="kpi.trend"
            class="kpi-trend text-caption"
            :class="kpi.trend > 0 ? 'text-error' : 'text-success'"
          >
            <VIcon size="12">{{ kpi.trend > 0 ? 'mdi-arrow-up' : 'mdi-arrow-down' }}</VIcon>
            {{ Math.abs(kpi.trend) }}%
          </span>
        </div>
      </div>
    </VCardText>

    <!-- Last Action -->
    <VCardText v-if="lastAction" class="pt-0">
      <div class="d-flex align-center gap-2 text-caption" style="opacity: 0.7">
        <VIcon size="14">mdi-history</VIcon>
        <span>{{ lastAction.text }}</span>
        <span v-if="lastAction.timestamp" class="ml-auto">{{ lastAction.timestamp }}</span>
      </div>
    </VCardText>

    <!-- Slot for custom content / quick actions -->
    <slot />
  </VCard>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import StatusBadge from './StatusBadge.vue'

const { t } = useI18n()

const props = defineProps({
  agent: {
    type: String,
    required: true,
    validator: v => ['centinela', 'jefe', 'supervisor', 'ingeniero'].includes(v),
  },
  status: {
    type: String,
    default: 'monitoring',
    validator: v => ['active', 'monitoring', 'acting', 'idle', 'error'].includes(v),
  },
  summary: { type: String, default: '' },
  kpis: { type: Array, default: () => [] },
  lastAction: { type: Object, default: null },
})

const AGENTS = {
  centinela:  { key: 'dashboard.agents.centinela',  icon: 'mdi-shield-lock-outline',       color: 'agent-centinela' },
  jefe:       { key: 'dashboard.agents.jefe',        icon: 'mdi-truck-outline',             color: 'agent-jefe' },
  supervisor: { key: 'dashboard.agents.supervisor',   icon: 'mdi-clipboard-check-outline',   color: 'agent-supervisor' },
  ingeniero:  { key: 'dashboard.agents.ingeniero',    icon: 'mdi-office-building-cog-outline', color: 'agent-ingeniero' },
}

const STATUS_MAP = {
  active:     { severity: 'ok',       key: 'dashboard.agentStatus.active' },
  monitoring: { severity: 'info',     key: 'dashboard.agentStatus.monitoring' },
  acting:     { severity: 'medium',   key: 'dashboard.agentStatus.acting' },
  idle:       { severity: 'low',      key: 'dashboard.agentStatus.idle' },
  error:      { severity: 'critical', key: 'dashboard.agentStatus.error' },
}

const agentLabel    = computed(() => AGENTS[props.agent]?.key ? t(AGENTS[props.agent].key) : props.agent)
const agentIcon     = computed(() => AGENTS[props.agent]?.icon  || 'mdi-robot-outline')
const agentColor    = computed(() => AGENTS[props.agent]?.color || 'primary')
const statusSeverity = computed(() => STATUS_MAP[props.status]?.severity || 'info')
const statusLabel    = computed(() => STATUS_MAP[props.status]?.key ? t(STATUS_MAP[props.status].key) : props.status)
const isLive         = computed(() => ['active', 'monitoring', 'acting'].includes(props.status))
</script>

<style scoped>
.kpi-item {
  min-width: 80px;
}
.kpi-value {
  line-height: 1.2;
}
.kpi-trend {
  font-size: 0.7rem;
}
</style>
